import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { trackEvent } from '../lib/analytics';
import { ChevronLeft, Check, ArrowRight, User, Phone, Mail, ClipboardList, Truck, FileCheck, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Component to display total cost with real-time calculation including GST
// Flat-rate pricing (Feb 2026): one $120 establishment fee per booking + per-animal rate.
const TotalCostDisplay = ({ selectedServices }) => {
  const [pricing, setPricing] = useState({ 
    totalExclGst: 0, 
    gst: 0, 
    totalInclGst: 0,
    serviceBreakdown: [],
    establishmentFee: 0
  });

  useEffect(() => {
    const calculateTotal = async () => {
      const hasValidCounts = Object.values(selectedServices).some(count => count > 0);
      
      if (!hasValidCounts) {
        setPricing({ totalExclGst: 0, gst: 0, totalInclGst: 0, serviceBreakdown: [], establishmentFee: 0 });
        return;
      }
      
      let subtotal = 0;
      let serviceBreakdown = [];
      let hasPriceableService = false;
      
      for (const [serviceId, numAnimals] of Object.entries(selectedServices)) {
        if (serviceId === 'special-projects') continue;
        
        if (numAnimals > 0) {
          try {
            const response = await axios.post(`${API}/calculate-price?num_animals=${numAnimals}&service_id=${serviceId}`);
            const servicePrice = response.data.subtotal;
            hasPriceableService = true;
            
            const serviceName = serviceId
              .split('-')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            
            serviceBreakdown.push({
              name: serviceName,
              animals: numAnimals,
              price: servicePrice,
              perAnimal: response.data.per_animal_cost
            });
            
            subtotal += servicePrice;
          } catch (error) {
            console.error('Failed to calculate price:', error);
          }
        }
      }
      
      const establishmentFee = hasPriceableService ? 120 : 0;

      // Combined Verification Credit: when Herd Read is booked alongside Liveweight,
      // DNA, or BCS, those animals are already verified — don't double-charge at
      // the herd-read rate.
      const liveweightCount = selectedServices['cattle-weighing'] || 0;
      const dnaCount = selectedServices['dna-tissue-sampling'] || 0;
      const bcsCount = selectedServices['bcs'] || 0;
      const herdReadCount = selectedServices['herd-read'] || 0;
      const verifiedElsewhere = liveweightCount + dnaCount + bcsCount;
      let combinedCredit = 0;
      let creditAnimals = 0;
      if (herdReadCount > 0 && verifiedElsewhere > 0) {
        creditAnimals = Math.min(herdReadCount, verifiedElsewhere);
        // Retain $1/head herd admin fee on animals already verified elsewhere
        const perAnimalCredit = 3.15 - 1.00;
        combinedCredit = Number((creditAnimals * perAnimalCredit).toFixed(2));
      }

      const totalExclGst = subtotal + establishmentFee - combinedCredit;
      const gst = totalExclGst * 0.15;
      const totalInclGst = totalExclGst + gst;

      setPricing({ totalExclGst, gst, totalInclGst, serviceBreakdown, establishmentFee, combinedCredit, creditAnimals });
    };

    calculateTotal();
  }, [selectedServices]);

  return (
    <div className="bg-gradient-to-r from-[#ff6b00]/10 to-transparent border-l-4 border-[#ff6b00] rounded-r-xl p-6 space-y-4">
      <div className="text-center mb-4">
        <p className="text-lg text-[#1a4d2e] font-bold">Your Custom PriceHub Package</p>
      </div>
      
      <div className="space-y-2">
        {pricing.serviceBreakdown.map((service, idx) => (
          <div key={idx} className="flex justify-between items-center text-sm">
            <span className="text-[#4a4a4a]">{service.name}</span>
            <span className="font-mono font-semibold text-[#1a4d2e]">
              ${service.price.toFixed(2)}
            </span>
          </div>
        ))}

        {pricing.combinedCredit > 0 && (
          <div className="flex justify-between items-center text-sm bg-green-50 border border-green-200 rounded p-2">
            <span className="text-green-800 font-medium">Verification Credit</span>
            <span className="font-mono font-semibold text-green-800">
              -${pricing.combinedCredit.toFixed(2)}
            </span>
          </div>
        )}

        {pricing.establishmentFee > 0 && (
          <div className="flex justify-between items-center text-sm pt-2 border-t border-[#e5ddd5]">
            <span className="text-[#4a4a4a]">Establishment Fee (one-off)</span>
            <span className="font-mono font-semibold text-[#1a4d2e]">${pricing.establishmentFee.toFixed(2)}</span>
          </div>
        )}
        
        <div className="flex justify-between items-center text-sm pt-2 border-t border-[#e5ddd5]">
          <span className="text-[#4a4a4a] font-medium">Total (excl. GST)</span>
          <span className="font-mono font-semibold text-[#1a4d2e]">${pricing.totalExclGst.toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between items-center text-sm">
          <span className="text-[#4a4a4a]">GST (15%)</span>
          <span className="font-mono font-semibold text-[#1a4d2e]">${pricing.gst.toFixed(2)}</span>
        </div>

        <div className="flex justify-between items-center text-base font-bold pt-2 border-t-2 border-[#1a4d2e]">
          <span className="text-[#1a4d2e]">Overall Total (incl. GST)</span>
          <span className="font-mono text-[#ff6b00]">${pricing.totalInclGst.toFixed(2)}</span>
        </div>
      </div>

      <div className="bg-gradient-to-r from-[#ff6b00] to-[#ff8533] rounded-lg p-4 shadow-lg">
        <div className="text-center mb-1">
          <span className="text-white text-xs uppercase tracking-wider font-semibold">
            Your Total Price (excl. GST)
          </span>
        </div>
        <div className="flex justify-center items-center">
          <motion.span 
            key={pricing.totalExclGst}
            initial={{ scale: 1.15, opacity: 0.8 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="font-mono text-4xl font-bold text-white"
          >
            ${pricing.totalExclGst.toFixed(2)}
          </motion.span>
        </div>
        <div className="text-center mt-1">
          <span className="text-white/90 text-sm font-medium">+ GST — No Hidden Costs</span>
        </div>
      </div>
    </div>
  );
};

const ProcessExplainerModal = ({ open, onOpenChange }) => {
  const steps = [
    {
      icon: ClipboardList,
      title: 'We gather your records',
      desc: 'Before the visit, we work with you to obtain your animal data from your existing systems or files. This ensures every animal is set up and ready before we arrive.'
    },
    {
      icon: Truck,
      title: 'We arrive ready to work',
      desc: 'Our team comes fully prepared with everything needed to complete the job efficiently and safely, with animal welfare as a priority.'
    },
    {
      icon: Check,
      title: 'We do the work on-farm',
      desc: 'Weighing, DNA sampling, condition scoring and herd read — every animal is checked, verified against your records, and all IDs are updated. Missing tags are replaced and any issues or mismatches are clearly flagged.'
    },
    {
      icon: FileCheck,
      title: 'You get a clear report',
      desc: 'A straightforward results and variance report is provided back to you — simple to understand and ready to act on.'
    },
    {
      icon: Upload,
      title: 'We close the loop for you',
      desc: 'We return clean, corrected herd data ready to be updated into your management system, so the job is fully completed end-to-end.'
    }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0" data-testid="process-explainer-modal">
        <div className="bg-gradient-to-r from-[#1a4d2e] to-[#2d6b47] text-white p-6">
          <DialogHeader>
            <DialogTitle className="text-2xl sm:text-3xl font-heading text-white flex items-center gap-3">
              <span className="bg-[#ff6b00] w-2 h-9 rounded"></span>
              We Handle The Whole Job
            </DialogTitle>
          </DialogHeader>
          <p className="text-white/90 mt-3 text-sm sm:text-base">
            From the moment you book until your data is back in your system — <strong>we do it for you.</strong>
          </p>
        </div>

        <div className="p-6 space-y-4">
          {steps.map(({ icon: Icon, title, desc }, idx) => (
            <div key={idx} className="flex items-start gap-4">
              <div className="flex-shrink-0 w-11 h-11 rounded-full bg-[#ff6b00]/10 border-2 border-[#ff6b00] flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#ff6b00]" />
              </div>
              <div className="flex-1">
                <h4 className="font-heading font-semibold text-[#1a4d2e] mb-1">{title}</h4>
                <p className="text-sm text-[#4a4a4a] leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}

          <div className="mt-6 p-4 bg-gradient-to-r from-[#ff6b00]/10 to-transparent border-l-4 border-[#ff6b00] rounded-r-md">
            <p className="text-[#1a4d2e] font-semibold text-sm">
              One price. One visit. Every animal checked and verified. We close the loop.
            </p>
          </div>

          <Button
            onClick={() => onOpenChange(false)}
            className="w-full h-12 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-base font-heading rounded-md mt-2"
            data-testid="process-explainer-close-btn"
          >
            Got it — back to my quote
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BookingPage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processModalShown, setProcessModalShown] = useState(false);
  
  // Two-stage registration
  const [isRegistered, setIsRegistered] = useState(false);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [otpStage, setOtpStage] = useState(false);          // showing the code entry screen?
  const [otpCode, setOtpCode] = useState('');
  const [otpResendCountdown, setOtpResendCountdown] = useState(0);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [lightReg, setLightReg] = useState({ name: '', phone: '', email: '', website: '' });
  
  // Step: 'register' | 'pricehub' | 'details'
  const [currentStep, setCurrentStep] = useState('register');

  // Quick Enquiry (new default PriceHub flow)
  const [enquiryServices, setEnquiryServices] = useState(new Set());
  const [enquiryForm, setEnquiryForm] = useState({
    num_animals: '',
    preferred_date: '',
    time_window: 'AM',
    time_slot: '',
    notes: '',
  });
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const [enquirySuccess, setEnquirySuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    farm_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    postcode: '',
    region: '',
    preferred_date: '',
    preferred_time: '',
    notes: '',
    // New farm-data fields (Feb 2026)
    herd_code: '',
    nait_number: '',
    data_source: '',
    data_provider: ''
  });

  const regions = [
    'Northland', 'Auckland', 'Waikato - BOP', 'Taranaki',
    'King Country - Manawatu', 'Wairarapa', 'Hawkes Bay',
    'Horowhenua - Wellington', 'Nelson - Marlborough',
    'West Coast', 'Canterbury', 'Otago', 'Southland'
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    document.title = 'PriceHub — Measured Livestock';

    const savedRegistration = sessionStorage.getItem('customerRegistration');
    if (savedRegistration) {
      try {
        const regData = JSON.parse(savedRegistration);
        // Only auto-restore sessions that completed OTP verification.
        // Old (pre-OTP) sessions or sessions without a token are discarded.
        if (!regData.verification_token) {
          sessionStorage.removeItem('customerRegistration');
        } else {
          setLightReg({ name: regData.customerName || regData.name || '', phone: regData.phone || '', email: regData.email || '', website: '' });
          setIsRegistered(true);
          setCurrentStep('pricehub');
          setFormData(prev => ({
            ...prev,
            contact_name: regData.customerName || regData.name || '',
            email: regData.email || '',
            phone: regData.phone || '',
            farm_name: regData.farmName || ''
          }));
        }
      } catch (error) {
        sessionStorage.removeItem('customerRegistration');
      }
    }

    fetchServices();
  }, []);

  // Countdown for OTP resend cooldown
  useEffect(() => {
    if (otpResendCountdown <= 0) return;
    const t = setTimeout(() => setOtpResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendCountdown]);

  // Track service changes to reset pricing visibility
  const prevServicesRef = React.useRef();
  useEffect(() => {
    const changed = prevServicesRef.current && 
      JSON.stringify(prevServicesRef.current) !== JSON.stringify(selectedServices);
    if (showPricing && changed) {
      setShowPricing(false);
      toast.info('Services updated! Click "Get Quote" to see updated pricing.', { duration: 4000 });
    }
    prevServicesRef.current = { ...selectedServices };
  }, [selectedServices, showPricing]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    }
  };

  const handleServiceToggle = (serviceId) => {
    setSelectedServices(prev => {
      const newSelected = { ...prev };
      if (serviceId in newSelected) {
        delete newSelected[serviceId];
      } else {
        const contactOnlyServices = ['special-projects'];
        newSelected[serviceId] = contactOnlyServices.includes(serviceId) ? 0 : 50;
      }
      return newSelected;
    });
  };

  const handleAnimalCountChange = (serviceId, count) => {
    setSelectedServices(prev => ({ ...prev, [serviceId]: parseInt(count) || 0 }));
  };

  // Stage 1A — Send the verification code to the email entered
  const handleLightRegistration = async (e) => {
    e.preventDefault();

    if (!lightReg.name.trim() || !lightReg.email.trim() || !lightReg.phone.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(lightReg.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setRegistrationLoading(true);
    try {
      // OTP TEMPORARILY DISABLED (per user request — Resend domain not verified yet).
      // Skip the verification code round-trip and let farmers hit the Quick Enquiry
      // form directly after Stage 1. The /api/enquiry/quick endpoint does not require
      // a verification token, so this is safe.
      const regData = {
        customerName: lightReg.name,
        email: lightReg.email,
        farmName: '',
        phone: lightReg.phone,
        isQualified: true,
        website: lightReg.website,
      };
      sessionStorage.setItem('customerRegistration', JSON.stringify(regData));

      setFormData(prev => ({ ...prev, contact_name: lightReg.name, email: lightReg.email, phone: lightReg.phone }));
      setIsRegistered(true);
      setOtpStage(false);
      setCurrentStep('pricehub');
      trackEvent('reg_complete');
      toast.success(`Welcome ${lightReg.name.split(' ')[0]}! Tell us what you need and we'll come back to you.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setRegistrationLoading(false);
    }
  };

  // Stage 1B — Verify the OTP and finalise registration
  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    if (!/^\d{6}$/.test(otpCode)) {
      toast.error('Please enter the 6-digit code from your email.');
      return;
    }
    setOtpVerifying(true);
    try {
      const verifyRes = await axios.post(`${API}/auth/verify-otp`, {
        email: lightReg.email,
        code: otpCode,
      });
      const token = verifyRes.data.verification_token;

      const regData = {
        customerName: lightReg.name,
        email: lightReg.email,
        farmName: '',
        phone: lightReg.phone,
        isQualified: true,
        website: lightReg.website,
        verification_token: token,
      };
      sessionStorage.setItem('customerRegistration', JSON.stringify(regData));
      await axios.post(`${API}/registrations`, regData);

      setFormData(prev => ({ ...prev, contact_name: lightReg.name, email: lightReg.email, phone: lightReg.phone }));
      setIsRegistered(true);
      setOtpStage(false);
      setCurrentStep('pricehub');
      trackEvent('reg_complete');
      toast.success(`Welcome ${lightReg.name.split(' ')[0]}! Browse our services and get your quote.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Could not verify the code. Please try again.';
      toast.error(msg);
    } finally {
      setOtpVerifying(false);
    }
  };

  // Resend code (with cooldown)
  const handleResendOtp = async () => {
    if (otpResendCountdown > 0) return;
    setOtpVerifying(true);
    try {
      await axios.post(`${API}/auth/send-otp`, {
        customerName: lightReg.name,
        email: lightReg.email,
        phone: lightReg.phone,
        website: lightReg.website,
      });
      setOtpResendCountdown(60);
      toast.success('New code sent. Check your email.');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Could not resend the code.';
      toast.error(msg);
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleGetQuote = async () => {
    if (Object.keys(selectedServices).length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    
    try {
      await axios.post(`${API}/registrations/quote-view`, { email: lightReg.email || formData.email });
    } catch (error) {
      console.log('Quote view tracking failed (non-critical):', error);
    }
    
    setShowPricing(true);
    trackEvent('quote_calculated');
    toast.success('Quote calculated! Review your pricing below.');

    // Show the "what's included end-to-end" explainer once per session
    if (!processModalShown) {
      setTimeout(() => {
        setShowProcessModal(true);
        setProcessModalShown(true);
      }, 700);
    }
  };

  // Quick Enquiry: build time-slot list within chosen AM/PM window
  const getTimeSlots = (window) => {
    if (window === 'AM') {
      // Post AM milking — bookings 7:00 to 13:00 (half-hour increments)
      const slots = [];
      for (let h = 7; h <= 13; h++) {
        slots.push(`${String(h).padStart(2, '0')}:00`);
        if (h !== 13) slots.push(`${String(h).padStart(2, '0')}:30`);
      }
      return slots;
    }
    // PM: 16:00 to 18:30
    const slots = [];
    for (let h = 16; h <= 18; h++) {
      slots.push(`${String(h).padStart(2, '0')}:00`);
      slots.push(`${String(h).padStart(2, '0')}:30`);
    }
    return slots;
  };

  const handleEnquiryServiceToggle = (serviceId) => {
    setEnquiryServices((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) next.delete(serviceId);
      else next.add(serviceId);
      return next;
    });
  };

  const handleEnquiryWindowChange = (win) => {
    setEnquiryForm((prev) => ({ ...prev, time_window: win, time_slot: '' }));
  };

  const handleQuickEnquirySubmit = async (e) => {
    e.preventDefault();
    if (enquiryServices.size === 0) {
      toast.error('Please tick at least one service.');
      return;
    }
    const animals = parseInt(enquiryForm.num_animals, 10);
    if (!animals || animals < 1) {
      toast.error('Please enter the number of animals.');
      return;
    }
    if (!enquiryForm.preferred_date) {
      toast.error('Please choose a preferred date.');
      return;
    }
    if (!enquiryForm.time_slot) {
      toast.error('Please pick a preferred time.');
      return;
    }

    setEnquirySubmitting(true);
    try {
      await axios.post(`${API}/enquiry/quick`, {
        contact_name: lightReg.name,
        email: lightReg.email,
        phone: lightReg.phone,
        services: Array.from(enquiryServices),
        num_animals: animals,
        preferred_date: enquiryForm.preferred_date,
        time_window: enquiryForm.time_window,
        time_slot: enquiryForm.time_slot,
        notes: enquiryForm.notes,
      });
      trackEvent('quick_enquiry_submitted');
      setEnquirySuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not submit enquiry. Please try again.');
    } finally {
      setEnquirySubmitting(false);
    }
  };

  // Carry the ticked services + animal count into the full-details flow
  const handleOpenFullDetails = () => {
    if (enquiryServices.size === 0) {
      toast.error('Tick at least one service first.');
      return;
    }
    const animals = parseInt(enquiryForm.num_animals, 10) || 50;
    const sel = {};
    enquiryServices.forEach((sid) => {
      sel[sid] = sid === 'special-projects' ? 0 : animals;
    });
    setSelectedServices(sel);
    setFormData((prev) => ({
      ...prev,
      preferred_date: enquiryForm.preferred_date || prev.preferred_date,
      preferred_time: enquiryForm.time_slot || prev.preferred_time,
      notes: enquiryForm.notes || prev.notes,
    }));
    setCurrentStep('details');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReviseQuote = () => {
    setShowPricing(false);
    toast.info('You can now revise your service selections');
    const serviceSection = document.querySelector('[data-section="service-selection"]');
    if (serviceSection) serviceSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // User clicks "Proceed" → go to details step
  const handleProceedToDetails = () => {
    if (Object.keys(selectedServices).length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    setCurrentStep('details');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Stage 2: Submit with full details
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.farm_name || !formData.address || !formData.region || !formData.preferred_date) {
      toast.error('Please complete all required fields');
      return;
    }
    
    if (!formData.herd_code.trim() && !formData.nait_number.trim()) {
      toast.error('Please provide a Herd code and/or NAIT number so we can match your animals to your records.');
      return;
    }
    
    if (Object.keys(selectedServices).length === 0) {
      toast.error('Please select at least one service');
      return;
    }

    setLoading(true);
    
    try {
      const serviceSelections = Object.entries(selectedServices).map(([serviceId, numAnimals]) => ({
        service_id: serviceId,
        num_animals: parseInt(numAnimals)
      }));

      const bookingData = { ...formData, services: serviceSelections };
      await axios.post(`${API}/bookings`, bookingData);
      trackEvent('booking_submitted');
      
      // Update registration with farm name
      const savedReg = JSON.parse(sessionStorage.getItem('customerRegistration') || '{}');
      savedReg.farmName = formData.farm_name;
      sessionStorage.setItem('customerRegistration', JSON.stringify(savedReg));
      
      toast.success("Quote request received! We'll be in touch soon to discuss your needs.");
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ==========================================
  // RENDER: Stage 1 - Light Registration
  // ==========================================
  if (currentStep === 'register') {
    return (
      <div className="min-h-screen bg-[#f4f4f0] py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-6 text-[#1a4d2e] hover:text-[#ff6b00] hover:bg-[#ff6b00]/10"
              data-testid="back-to-home-button"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
            
            <div className="text-center mb-8">
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#1a4d2e] mb-3 tracking-tight">
                PriceHub
              </h1>
              <p className="text-[#8d6e63] text-lg max-w-xl mx-auto">
                {otpStage
                  ? 'Enter the 6-digit code we just emailed you.'
                  : 'Tell us what you need and we\'ll come back to you to confirm.'}
              </p>
            </div>
          </motion.div>

          {otpStage ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <Card className="border-2 border-[#ff6b00] shadow-xl">
                <CardHeader className="bg-gradient-to-r from-[#1a4d2e] to-[#2d6b47] text-white pb-6">
                  <CardTitle className="text-2xl font-heading flex items-center gap-3">
                    <div className="bg-[#ff6b00] rounded-full p-2.5">
                      <Mail className="w-5 h-5" />
                    </div>
                    Verify Your Email
                  </CardTitle>
                  <p className="text-white/85 mt-1 text-sm">
                    We sent a 6-digit code to <strong>{lightReg.email}</strong>
                  </p>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleVerifyOtp} className="space-y-5">
                    <div>
                      <Label htmlFor="otp-code" className="text-[#1a4d2e] font-medium">
                        Verification Code
                      </Label>
                      <Input
                        id="otp-code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="123456"
                        className="mt-2 h-14 text-center text-3xl font-mono tracking-[0.5em] border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]"
                        data-testid="otp-code-input"
                        required
                        autoFocus
                      />
                      <p className="text-xs text-[#8d6e63] mt-2 text-center">
                        Code expires in 10 minutes. Check your spam folder if it doesn't arrive.
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={otpVerifying || otpCode.length !== 6}
                      className="w-full h-12 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-base font-heading rounded-md"
                      data-testid="verify-otp-btn"
                    >
                      {otpVerifying ? 'Verifying…' : 'Verify & Continue'}
                    </Button>

                    <div className="flex items-center justify-between text-sm pt-2 border-t border-[#f0ede5]">
                      <button
                        type="button"
                        onClick={() => { setOtpStage(false); setOtpCode(''); }}
                        className="text-[#8d6e63] hover:text-[#1a4d2e]"
                        data-testid="otp-change-email-btn"
                      >
                        ← Use a different email
                      </button>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={otpResendCountdown > 0 || otpVerifying}
                        className="text-[#ff6b00] hover:underline disabled:text-[#8d6e63] disabled:no-underline disabled:cursor-not-allowed"
                        data-testid="otp-resend-btn"
                      >
                        {otpResendCountdown > 0 ? `Resend in ${otpResendCountdown}s` : 'Resend code'}
                      </button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.5 }}>
            <Card className="border-2 border-[#1a4d2e] shadow-xl">
              <CardHeader className="bg-gradient-to-r from-[#1a4d2e] to-[#2d6b47] text-white pb-6">
                <CardTitle className="text-2xl font-heading flex items-center gap-3">
                  <div className="bg-white/20 rounded-full p-2.5">
                    <User className="w-5 h-5" />
                  </div>
                  Quick Registration
                </CardTitle>
                <p className="text-white/85 mt-1">
                  Enter your details to continue to the Quick Enquiry form
                </p>
              </CardHeader>
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={handleLightRegistration} className="space-y-5">
                  <div>
                    <Label htmlFor="reg-name" className="text-[#1a4d2e] font-semibold flex items-center gap-2">
                      <User className="w-4 h-4 text-[#ff6b00]" /> Your Name *
                    </Label>
                    <Input
                      id="reg-name"
                      value={lightReg.name}
                      onChange={(e) => setLightReg(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-2 h-12"
                      placeholder="Enter your full name"
                      data-testid="reg-name-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-phone" className="text-[#1a4d2e] font-semibold flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[#ff6b00]" /> Phone Number *
                    </Label>
                    <Input
                      id="reg-phone"
                      type="tel"
                      value={lightReg.phone}
                      onChange={(e) => setLightReg(prev => ({ ...prev, phone: e.target.value }))}
                      className="mt-2 h-12"
                      placeholder="021 123 4567"
                      data-testid="reg-phone-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-email" className="text-[#1a4d2e] font-semibold flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#ff6b00]" /> Email Address *
                    </Label>
                    <Input
                      id="reg-email"
                      type="email"
                      value={lightReg.email}
                      onChange={(e) => setLightReg(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-2 h-12"
                      placeholder="your@email.com"
                      data-testid="reg-email-input"
                      required
                    />
                  </div>

                  {/* Honeypot — hidden from real users, bots fill every input */}
                  <div
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
                  >
                    <label htmlFor="website-hp">Website (leave blank)</label>
                    <input
                      type="text"
                      id="website-hp"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={lightReg.website}
                      onChange={(e) => setLightReg({ ...lightReg, website: e.target.value })}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={registrationLoading}
                    className="w-full bg-[#ff6b00] hover:bg-[#e55a00] text-white text-lg py-6 font-heading"
                    data-testid="access-pricehub-btn"
                  >
                    {registrationLoading ? (
                      <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>Sending code...</>
                    ) : (
                      <>Continue to Enquiry <ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
          )}

          <motion.div className="mt-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.5 }}>
            <div className="bg-gradient-to-r from-[#ff6b00]/10 via-[#ff6b00]/5 to-transparent border-l-4 border-[#ff6b00] p-5 rounded-r-lg">
              <div className="flex items-start space-x-3">
                <div className="text-2xl">💡</div>
                <div>
                  <p className="text-[#1a4d2e] font-semibold text-lg mb-1">Save Time and Money</p>
                  <p className="text-[#4a4a4a]">Keep your cows on grass longer by booking multiple services in one visit.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER: PriceHub — Quick Enquiry Form (Feb 2026)
  // ==========================================
  if (currentStep === 'pricehub') {
    if (enquirySuccess) {
      return (
        <div className="min-h-screen bg-[#f4f4f0] py-16">
          <div className="container mx-auto px-4 max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Card className="border-2 border-[#1a4d2e] shadow-2xl overflow-hidden" data-testid="enquiry-success-card">
                <div className="bg-gradient-to-r from-[#1a4d2e] to-[#2d6b47] p-8 text-center text-white">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#ff6b00] flex items-center justify-center mb-4">
                    <Check className="w-9 h-9" />
                  </div>
                  <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-2">Thanks, {lightReg.name.split(' ')[0]}!</h1>
                  <p className="text-white/90 text-base">Your enquiry has landed with the team.</p>
                </div>
                <CardContent className="p-8 space-y-5">
                  <p className="text-[#1a4d2e] font-semibold text-lg text-center">
                    We'll come back to you shortly to confirm your visit.
                  </p>
                  <div className="bg-[#f4f4f0] border border-[#e5ddd5] rounded-lg p-5 space-y-2 text-sm text-[#4a4a4a]">
                    <div className="flex justify-between"><span className="font-semibold text-[#1a4d2e]">Name</span><span>{lightReg.name}</span></div>
                    <div className="flex justify-between"><span className="font-semibold text-[#1a4d2e]">Email</span><span>{lightReg.email}</span></div>
                    <div className="flex justify-between"><span className="font-semibold text-[#1a4d2e]">Phone</span><span>{lightReg.phone}</span></div>
                    <div className="flex justify-between gap-4">
                      <span className="font-semibold text-[#1a4d2e] whitespace-nowrap">Services requested</span>
                      <span className="text-right">
                        {Array.from(enquiryServices)
                          .map(sid => services.find(s => s.id === sid)?.name || sid)
                          .join(', ')}
                      </span>
                    </div>
                    <div className="flex justify-between"><span className="font-semibold text-[#1a4d2e]">Animals</span><span>{enquiryForm.num_animals}</span></div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-[#1a4d2e]">Preferred date</span>
                      <span>
                        {enquiryForm.preferred_date
                          ? (() => {
                              const [y, m, d] = enquiryForm.preferred_date.split('-');
                              return `${d}/${m}/${y}`;
                            })()
                          : ''}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-[#1a4d2e]">Preferred time</span>
                      <span>
                        {enquiryForm.time_window === 'AM' ? 'Post AM Milking' : 'Pre / Post PM Milking'} · {enquiryForm.time_slot}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={() => navigate('/')}
                      className="flex-1 h-12 bg-[#1a4d2e] hover:bg-[#2d6b47] text-white font-heading"
                      data-testid="enquiry-done-home-btn"
                    >
                      Back to Home
                    </Button>
                    <Button
                      onClick={() => {
                        setEnquirySuccess(false);
                        setEnquiryServices(new Set());
                        setEnquiryForm({ num_animals: '', preferred_date: '', time_window: 'AM', time_slot: '', notes: '' });
                      }}
                      variant="outline"
                      className="flex-1 h-12 border-2 border-[#ff6b00] text-[#ff6b00] hover:bg-[#ff6b00] hover:text-white font-heading"
                      data-testid="enquiry-another-btn"
                    >
                      Submit Another Enquiry
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      );
    }

    const timeSlots = getTimeSlots(enquiryForm.time_window);
    const windowCopy = enquiryForm.time_window === 'AM'
      ? { title: 'Post Milking AM — Morning', detail: 'Morning milking normally finishes from 7am. Bookings available between 7:00am – 1:00pm.' }
      : { title: 'Pre or Post Milking PM — Afternoon', detail: 'Afternoon milking normally starts 2pm – 4:30pm. Bookings available between 4:00pm – 6:30pm.' };

    return (
      <div className="min-h-screen bg-[#f4f4f0] py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-[#1a4d2e] hover:text-[#ff6b00] hover:bg-[#ff6b00]/10"
                data-testid="back-to-home-button"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
              <button
                onClick={() => {
                  sessionStorage.removeItem('customerRegistration');
                  setLightReg({ name: '', phone: '', email: '', website: '' });
                  setIsRegistered(false);
                  setOtpStage(false);
                  setOtpCode('');
                  setCurrentStep('register');
                  setEnquiryServices(new Set());
                  setEnquiryForm({ num_animals: '', preferred_date: '', time_window: 'AM', time_slot: '', notes: '' });
                  toast.success('Signed out. Enter different details to start again.');
                  window.scrollTo({ top: 0, behavior: 'auto' });
                }}
                className="text-sm text-[#8d6e63] hover:text-[#ff6b00] underline-offset-2 hover:underline"
                data-testid="logout-pricehub-btn"
              >
                Not you? Sign out
              </button>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#1a4d2e] mb-2 tracking-tight">
              Welcome, {lightReg.name.split(' ')[0]}
            </h1>
            <p className="text-[#8d6e63] text-lg">
              Tell us what you need and when — we'll come back to you to confirm.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}>
            <Card className="border-2 border-[#1a4d2e] shadow-xl overflow-hidden" data-testid="quick-enquiry-card">
              <CardHeader className="bg-gradient-to-r from-[#1a4d2e] to-[#2d6b47] text-white">
                <CardTitle className="font-heading text-2xl sm:text-3xl flex items-center gap-2">
                  Quick Enquiry
                </CardTitle>
                <p className="text-sm text-white/90 mt-1">Tick services, add animal count, pick a date &amp; time — takes under a minute.</p>
              </CardHeader>
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={handleQuickEnquirySubmit} className="space-y-7">
                  {/* Services tick-boxes */}
                  <div>
                    <Label className="text-[#1a4d2e] font-semibold text-base block mb-3">
                      Which services are you interested in? <span className="text-[#ff6b00]">*</span>
                    </Label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {services.filter(s => s.id !== 'special-projects').map((service) => {
                        const checked = enquiryServices.has(service.id);
                        return (
                          <label
                            key={service.id}
                            className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                              checked
                                ? 'border-[#1a4d2e] bg-[#1a4d2e]/5 shadow-sm'
                                : 'border-[#e5ddd5] bg-white hover:border-[#8d6e63]/50'
                            }`}
                            data-testid={`enquiry-service-${service.id}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => handleEnquiryServiceToggle(service.id)}
                              data-testid={`enquiry-service-checkbox-${service.id}`}
                            />
                            <span className="flex-1 text-sm font-semibold text-[#1a4d2e] leading-tight">
                              {service.name}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Animal count */}
                  <div>
                    <Label htmlFor="enq-animals" className="text-[#1a4d2e] font-semibold">
                      How many animals? <span className="text-[#ff6b00]">*</span>
                    </Label>
                    <Input
                      id="enq-animals"
                      type="number"
                      min="1"
                      max="5000"
                      value={enquiryForm.num_animals}
                      onChange={(e) => setEnquiryForm(prev => ({ ...prev, num_animals: e.target.value }))}
                      placeholder="e.g. 350"
                      className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]"
                      data-testid="enquiry-num-animals-input"
                      required
                    />
                  </div>

                  {/* Preferred date */}
                  <div>
                    <Label htmlFor="enq-date" className="text-[#1a4d2e] font-semibold">
                      Preferred date <span className="text-[#ff6b00]">*</span>
                    </Label>
                    <Input
                      id="enq-date"
                      type="date"
                      value={enquiryForm.preferred_date}
                      onChange={(e) => setEnquiryForm(prev => ({ ...prev, preferred_date: e.target.value }))}
                      min={new Date().toISOString().slice(0, 10)}
                      className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]"
                      data-testid="enquiry-date-input"
                      required
                    />
                  </div>

                  {/* Time window */}
                  <div>
                    <Label className="text-[#1a4d2e] font-semibold block mb-3">
                      Preferred time window <span className="text-[#ff6b00]">*</span>
                    </Label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {['AM', 'PM'].map((win) => {
                        const active = enquiryForm.time_window === win;
                        return (
                          <button
                            key={win}
                            type="button"
                            onClick={() => handleEnquiryWindowChange(win)}
                            className={`text-left p-4 rounded-lg border-2 transition-all ${
                              active
                                ? 'border-[#ff6b00] bg-[#ff6b00]/5 shadow-sm'
                                : 'border-[#e5ddd5] bg-white hover:border-[#8d6e63]/50'
                            }`}
                            data-testid={`enquiry-window-${win.toLowerCase()}`}
                          >
                            <div className="font-heading font-bold text-[#1a4d2e]">
                              {win === 'AM' ? 'Post AM Milking' : 'Pre / Post PM Milking'}
                            </div>
                            <div className="text-xs text-[#8d6e63] mt-1">
                              {win === 'AM' ? 'Morning · 7:00am – 1:00pm' : 'Afternoon · 4:00pm – 6:30pm'}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-[#8d6e63] italic mt-2">{windowCopy.detail}</p>
                  </div>

                  {/* Specific time slot */}
                  <div>
                    <Label htmlFor="enq-time" className="text-[#1a4d2e] font-semibold">
                      Pick a time within that window <span className="text-[#ff6b00]">*</span>
                    </Label>
                    <Select
                      value={enquiryForm.time_slot}
                      onValueChange={(v) => setEnquiryForm(prev => ({ ...prev, time_slot: v }))}
                    >
                      <SelectTrigger id="enq-time" className="mt-2 h-12 border-[#e5ddd5]" data-testid="enquiry-time-slot">
                        <SelectValue placeholder="Select a time" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((slot) => (
                          <SelectItem key={slot} value={slot} data-testid={`enquiry-time-option-${slot}`}>
                            {slot}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Notes */}
                  <div>
                    <Label htmlFor="enq-notes" className="text-[#1a4d2e] font-semibold">
                      Anything else we should know? <span className="text-[#8d6e63] font-normal text-sm">(optional)</span>
                    </Label>
                    <Textarea
                      id="enq-notes"
                      rows={3}
                      value={enquiryForm.notes}
                      onChange={(e) => setEnquiryForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Yard setup, access, specific requirements…"
                      className="mt-2 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]"
                      data-testid="enquiry-notes-input"
                    />
                  </div>

                  {/* Submit */}
                  <div className="bg-gradient-to-r from-[#ff6b00]/10 to-transparent border-l-4 border-[#ff6b00] p-4 rounded-r-md">
                    <p className="text-sm text-[#1a4d2e]">
                      <strong>We'll come back to you</strong> within one business day to confirm the visit and work through anything else needed.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={enquirySubmitting}
                    className="w-full h-14 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-lg font-heading rounded-md shadow-lg"
                    data-testid="submit-quick-enquiry-btn"
                  >
                    {enquirySubmitting ? 'Sending…' : <>Submit Enquiry <ArrowRight className="w-5 h-5 ml-2" /></>}
                  </Button>

                  <div className="text-center pt-2 border-t border-[#f0ede5]">
                    <p className="text-sm text-[#8d6e63] mb-2">Already have your herd code &amp; farm details handy?</p>
                    <button
                      type="button"
                      onClick={handleOpenFullDetails}
                      className="text-sm font-semibold text-[#1a4d2e] hover:text-[#ff6b00] underline underline-offset-2"
                      data-testid="open-full-details-btn"
                    >
                      Book with full farm details instead →
                    </button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER: Stage 2 - Complete Details & Submit
  // ==========================================
  return (
    <div className="min-h-screen bg-[#f4f4f0] py-12">
      <div className="container mx-auto px-4 max-w-7xl">
        <motion.div className="mb-10" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Button
            variant="ghost"
            onClick={() => setCurrentStep('pricehub')}
            className="mb-6 text-[#1a4d2e] hover:text-[#ff6b00] hover:bg-[#ff6b00]/10"
            data-testid="back-to-pricehub-button"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Back to PriceHub
          </Button>
          
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#1a4d2e] mb-2 tracking-tight">
            Complete Your Booking
          </h1>
          <p className="text-[#8d6e63] text-lg">
            Fill in your farm details so we can arrange your visit
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Farm & Contact Details */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Card className="border-2 border-[#ff6b00] shadow-lg" data-testid="farm-details-card">
                  <CardHeader className="bg-gradient-to-r from-[#ff6b00]/10 to-transparent border-b border-[#ff6b00]">
                    <CardTitle className="font-heading text-2xl sm:text-3xl text-[#1a4d2e] flex items-center">
                      <span className="w-2 h-8 bg-[#ff6b00] mr-3 rounded"></span>
                      Farm & Contact Details
                    </CardTitle>
                    <p className="text-sm text-[#8d6e63] mt-2">
                      <strong className="text-[#ff6b00]">All required fields (*)</strong> must be completed to submit
                    </p>
                  </CardHeader>
                  <CardContent className="p-6 space-y-5">
                    <div>
                      <Label htmlFor="farm_name" className="text-[#1a4d2e] font-medium">Farm Name *</Label>
                      <Input id="farm_name" name="farm_name" required value={formData.farm_name} onChange={handleInputChange} className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]" data-testid="farm-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="contact_name" className="text-[#1a4d2e] font-medium">Contact Name *</Label>
                      <Input id="contact_name" name="contact_name" required value={formData.contact_name} onChange={handleInputChange} className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]" data-testid="contact-name-input" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email" className="text-[#1a4d2e] font-medium">Email *</Label>
                        <Input id="email" name="email" type="email" required value={formData.email} onChange={handleInputChange} className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]" data-testid="email-input" />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-[#1a4d2e] font-medium">Phone *</Label>
                        <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleInputChange} className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]" data-testid="phone-input" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="address" className="text-[#1a4d2e] font-medium">Site Address *</Label>
                      <Textarea id="address" name="address" required value={formData.address} onChange={handleInputChange} placeholder="Enter your farm site address..." className="mt-2 min-h-[80px] border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]" data-testid="address-input" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="postcode" className="text-[#1a4d2e] font-medium">Post Code</Label>
                        <Input id="postcode" name="postcode" value={formData.postcode} onChange={handleInputChange} placeholder="4-digit post code" maxLength={4} className="mt-2 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]" data-testid="postcode-input" />
                      </div>
                      <div>
                        <Label htmlFor="region" className="text-[#1a4d2e] font-medium">Region *</Label>
                        <Select value={formData.region} onValueChange={(value) => setFormData({ ...formData, region: value })}>
                          <SelectTrigger className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]" data-testid="region-select">
                            <SelectValue placeholder="Select your region" />
                          </SelectTrigger>
                          <SelectContent>
                            {regions.map((region) => <SelectItem key={region} value={region}>{region}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="preferred_date" className="text-[#1a4d2e] font-medium">Preferred Date *</Label>
                        <Input id="preferred_date" name="preferred_date" type="date" required value={formData.preferred_date} onChange={handleInputChange} className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]" data-testid="preferred-date-input" />
                      </div>
                      <div>
                        <Label htmlFor="preferred_time" className="text-[#1a4d2e] font-medium">Preferred Time</Label>
                        <select id="preferred_time" name="preferred_time" value={formData.preferred_time} onChange={handleInputChange} className="mt-2 w-full h-12 px-4 border-2 border-[#e5ddd5] rounded-md focus:border-[#ff6b00] focus:ring-[#ff6b00] focus:outline-none" data-testid="preferred-time-input">
                          <option value="">Select a time</option>
                          {['07:00','07:30','08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','16:00','16:30','17:00','17:30','18:00','18:30'].map(t => {
                            const h = parseInt(t.split(':')[0]);
                            const m = t.split(':')[1];
                            const label = h > 12 ? `${h-12}:${m} PM` : h === 12 ? `12:${m} PM` : `${h}:${m} AM`;
                            return <option key={t} value={t}>{label}</option>;
                          })}
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="notes" className="text-[#1a4d2e] font-medium">Additional Notes</Label>
                      <Textarea id="notes" name="notes" value={formData.notes} onChange={handleInputChange} placeholder="Any special requirements or notes..." className="mt-2 min-h-[100px] border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]" data-testid="notes-input" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Herd Data Card — Feb 2026 */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}>
                <Card className="border-2 border-[#1a4d2e] shadow-lg" data-testid="herd-data-card">
                  <CardHeader className="bg-gradient-to-r from-[#1a4d2e]/10 to-transparent border-b border-[#1a4d2e]">
                    <CardTitle className="font-heading text-2xl sm:text-3xl text-[#1a4d2e] flex items-center">
                      <span className="w-2 h-8 bg-[#1a4d2e] mr-3 rounded"></span>
                      Herd Data
                    </CardTitle>
                    <p className="text-sm text-[#8d6e63] mt-2">
                      Helps us line up with your existing records — at least one of <strong>Herd code</strong> or <strong>NAIT number</strong> is required.
                    </p>
                  </CardHeader>
                  <CardContent className="p-6 space-y-5">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="herd_code" className="text-[#1a4d2e] font-medium">Herd Code</Label>
                        <Input
                          id="herd_code"
                          name="herd_code"
                          value={formData.herd_code}
                          onChange={handleInputChange}
                          placeholder="e.g. 12345"
                          className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]"
                          data-testid="herd-code-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="nait_number" className="text-[#1a4d2e] font-medium">NAIT Number</Label>
                        <Input
                          id="nait_number"
                          name="nait_number"
                          value={formData.nait_number}
                          onChange={handleInputChange}
                          placeholder="e.g. 123456789"
                          className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]"
                          data-testid="nait-number-input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-[#8d6e63] -mt-2">
                      <span className="text-[#ff6b00] font-semibold">*</span> Provide a Herd code, a NAIT number, or both.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="data_source" className="text-[#1a4d2e] font-medium">Herd Data Source</Label>
                        <Select
                          value={formData.data_source}
                          onValueChange={(value) => setFormData({ ...formData, data_source: value })}
                        >
                          <SelectTrigger
                            className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]"
                            data-testid="data-source-select"
                          >
                            <SelectValue placeholder="Select source (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="MyHERD">MyHERD</SelectItem>
                            <SelectItem value="Minda">Minda</SelectItem>
                            <SelectItem value="NAIT">NAIT</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="data_provider" className="text-[#1a4d2e] font-medium">Data Provider</Label>
                        <Select
                          value={formData.data_provider}
                          onValueChange={(value) => setFormData({ ...formData, data_provider: value })}
                        >
                          <SelectTrigger
                            className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]"
                            data-testid="data-provider-select"
                          >
                            <SelectValue placeholder="Select provider (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="LIC">LIC</SelectItem>
                            <SelectItem value="CRV">CRV</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Submit - visible on mobile below form */}
              <div className="lg:hidden">
                <Button
                  type="submit"
                  disabled={loading || !formData.farm_name || !formData.address || !formData.region || !formData.preferred_date || (!formData.herd_code.trim() && !formData.nait_number.trim())}
                  className="w-full h-14 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-lg font-heading rounded-md"
                  data-testid="submit-booking-button-mobile"
                >
                  {loading ? 'Submitting...' : "Submit and Let's Talk"}
                </Button>
                <p className="text-xs text-[#8d6e63] text-center mt-3">
                  We'll get in touch to confirm the details
                </p>
              </div>
            </form>
          </div>

          {/* Sticky Summary - no submit button, summary only */}
          <div className="lg:col-span-1">
            <motion.div className="sticky top-8" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3, duration: 0.5 }}>
              <Card className="border-2 border-[#1a4d2e] shadow-2xl overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-[#ff6b00] to-[#ff8533] text-white">
                  <CardTitle className="font-heading text-2xl">Your Booking Summary</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6 bg-gradient-to-b from-white to-[#f4f4f0]/30">
                  {/* Selected Services */}
                  <div className="bg-gradient-to-br from-[#f4f4f0] to-white p-4 rounded-lg border border-[#e5ddd5]">
                    <p className="text-sm font-semibold text-[#1a4d2e] mb-3">Selected Services:</p>
                    <ul className="space-y-2">
                      {Object.entries(selectedServices).map(([serviceId, numAnimals]) => {
                        const service = services.find(s => s.id === serviceId);
                        return service ? (
                          <li key={serviceId} className="text-sm text-[#1a4d2e] flex items-start">
                            <Check className="w-4 h-4 mr-2 mt-0.5 text-[#ff6b00] flex-shrink-0" />
                            <div><div className="font-medium">{service.name}</div><div className="text-[#8d6e63] text-xs">{numAnimals} animals</div></div>
                          </li>
                        ) : null;
                      })}
                    </ul>
                  </div>

                  {/* Pricing display removed — see admin dashboard for pricing */}

                  {/* Completion Checklist */}
                  <div className="bg-gradient-to-br from-[#f4f4f0] to-white p-4 rounded-lg border border-[#e5ddd5]">
                    <p className="text-xs font-semibold text-[#1a4d2e] mb-2">Completion Checklist:</p>
                    <ul className="space-y-1.5 text-xs">
                      {[
                        { label: 'Farm Name', done: !!formData.farm_name },
                        { label: 'Contact Details', done: !!formData.contact_name && !!formData.email },
                        { label: 'Site Address', done: !!formData.address },
                        { label: 'Region', done: !!formData.region },
                        { label: 'Preferred Date', done: !!formData.preferred_date },
                        { label: 'Herd Code or NAIT', done: !!(formData.herd_code.trim() || formData.nait_number.trim()) },
                        { label: 'Herd Data Source', done: !!formData.data_source },
                        { label: 'Data Provider', done: !!formData.data_provider },
                      ].map(({ label, done }) => (
                        <li key={label} className={`flex items-center gap-2 ${done ? 'text-green-700' : 'text-gray-400'}`}>
                          {done ? <Check className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 border border-gray-300 rounded-sm inline-block" />}
                          {label}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <p className="text-xs text-[#8d6e63] text-center">
                    Complete all fields below then submit at the bottom of the page
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>

        {/* Submit Button — at the very bottom of the page, full width */}
        <motion.div 
          className="mt-10 max-w-7xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <Card className="border-2 border-[#1a4d2e] shadow-xl overflow-hidden hidden lg:block">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="text-xl font-heading font-bold text-[#1a4d2e]">Ready to submit?</h3>
                  <p className="text-sm text-[#8d6e63] mt-1">We'll review your booking request and get in touch to confirm</p>
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !formData.farm_name || !formData.address || !formData.region || !formData.preferred_date || (!formData.herd_code.trim() && !formData.nait_number.trim())}
                  className="w-full sm:w-auto h-14 px-10 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-lg font-heading rounded-md"
                  data-testid="submit-booking-button"
                >
                  {loading ? 'Submitting...' : "Submit and Let's Talk"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default BookingPage;
